import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GetUpgradeOptionsResponse } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { CheckoutPlan } from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import {
  StripeCurrentPlanCard,
  StripeSchoolLicenseCta,
  StripeUpgradeOptionCard,
} from '../../../components/StripePricingCards';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import useScreenSize from '../../../utils/useScreenSize';
import useAction from '../../../utils/useAction';
import { getUpgradeOptions } from '../../../actions/user.actions';
import { selectUser } from '../../../reducers/user.selectors';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import useRouter from '../../../utils/useRouter';
import { AmpliPremiumPlanSelectedPayload, ampliSubscriptionType, routes } from '../../../constants';
import {
  getStripeUpgradeCheckoutUrl,
  orderStripeUpgradeOptions,
  STRIPE_MOST_POPULAR_PLAN,
  STRIPE_PREMIUM_PLAN_SELECTED_INTERVAL,
} from '../utils';
import { REF_RABATT_CODE, REF_RABATT_PLANS } from '../../checkout/StripeCheckoutPage/checkout.constants';
import services from '../../../services';
import { PremiumPlanSelected } from '../../../ampli';

const StripeUpgradePage: React.FC = () => {
  const { t } = useTranslation();
  const { router } = useRouter();
  const { isMdScreen, isLgScreen } = useScreenSize();
  const isDesktopScreen = isMdScreen || isLgScreen;
  const user = useSelector(selectUser);
  const fetchUpgradeOptions = useAction(getUpgradeOptions);

  const [optionsResult, setOptionsResult] = useState<GetUpgradeOptionsResponse | null>(null);
  const [loadError, setLoadError] = useState<boolean>(false);

  // Redirect non-eligible users away. The only allowed cohort is users with a
  // mapped `payment.stripeCustomerId` — both existing Stripe subscribers
  // (in-place upgrade) and the rarer "Basis with a customer record" case
  // (previously-Stripe-subscribed users re-subscribing). Zuora users and
  // fresh Basis users (no Stripe customer record) get the legacy `/premium`
  // flow instead. Trialing users are also excluded — kimuno's upgrade
  // endpoint rejects mid-trial upgrades.
  useEffect(() => {
    if (!user) return;
    if (!user.isStripeUser) {
      router.push(`/${routes.dashboard.root}`);
      return;
    }
    if (user.isInTrial) {
      router.push(`/${routes.dashboard.root}`);
    }
  }, [user, router]);

  useEffect(() => {
    // Wait until `user` resolves and the cohort is confirmed-eligible before
    // firing the kimuno request. Without this, an ineligible user (Zuora,
    // fresh basis, trialing) would still hit `/upgrade-options` on mount —
    // wasting a request and potentially leaving us with a transient
    // `loadError` before the redirect effect above kicks them out.
    if (!user) return;
    if (!user.isStripeUser) return;
    if (user.isInTrial) return;

    let active = true;
    fetchUpgradeOptions()
      .then((result) => {
        if (!active) return;
        setOptionsResult(result);
      })
      .catch(() => {
        if (!active) return;
        setLoadError(true);
      });
    return () => {
      active = false;
    };
    // `fetchUpgradeOptions` is a fresh wrapper from `useAction` on every
    // render, so including it would re-fire the effect forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isStripeUser, user?.isInTrial]);

  // SM-293. Mirrors the Zuora `sendPremiumPlanSelectedEvent` in
  // PricingPage.tsx/NewPricingPage.tsx. Stripe plans have no numeric Zuora
  // licence id, so `product name` carries the plan key instead — matching
  // the convention already used by the backend Stripe analytics events
  // (e.g. `packages/analytics/src/events/subscription-reactivated.ts`).
  const sendPremiumPlanSelectedEvent = (targetPlanKey: CheckoutPlan, isRefRabatt: boolean) => {
    if (!user) return;
    const payload: AmpliPremiumPlanSelectedPayload = {
      'product name': targetPlanKey,
      'subscription interval': STRIPE_PREMIUM_PLAN_SELECTED_INTERVAL,
      'subscription type': isRefRabatt ? ampliSubscriptionType.REF : ampliSubscriptionType.TEACHER,
    };
    services.track.ampliEventTrack({ event: new PremiumPlanSelected(payload) });
  };

  const handleSelectPlan = (targetPlanKey: CheckoutPlan) => {
    sendPremiumPlanSelectedEvent(targetPlanKey, false);
    // SM-202. Basis-with-Stripe users have no active subscription, so the
    // Confirm page (which renders proration + saved payment method off an
    // existing sub) shows zeros and the wrong-shaped payment widget. Route
    // them straight to the inline-Elements resubscribe checkout instead.
    if (user?.isBasisUser && user?.isStripeUser) {
      router.push(getStripeUpgradeCheckoutUrl(targetPlanKey));
      return;
    }
    // Paid Stripe users: mirrors the Zuora pattern — click on the list page
    // navigates to the confirmation page; the actual upgrade mutation only
    // fires from there.
    router.push(
      `/${routes.stripeUpgrade.root}/${routes.stripeUpgradeConfirm.root}?targetPlanKey=${targetPlanKey}`,
    );
  };

  // SM-415: Ref-Rabatt shortcut, mirroring the Zuora `/premium` secondary CTA.
  // Only basis-with-Stripe users route to the resubscribe checkout — the sole
  // destination with a working Ref-Rabatt toggle — and only pro/fullAccess
  // carry the discount, so the button is scoped to that cohort + those plans.
  const handleRefRabatt = (targetPlanKey: CheckoutPlan) => {
    sendPremiumPlanSelectedEvent(targetPlanKey, true);
    router.push(getStripeUpgradeCheckoutUrl(targetPlanKey, REF_RABATT_CODE));
  };

  const getRefRabattCta = (targetPlanKey: CheckoutPlan) => {
    const eligible =
      user?.isBasisUser && user?.isStripeUser && REF_RABATT_PLANS.includes(targetPlanKey);
    if (!eligible) return undefined;
    return {
      label: t('{{discount}}% Trainee Discount', { discount: 50 }),
      onClick: () => handleRefRabatt(targetPlanKey),
    };
  };

  const handleSchoolLicenseClick = () => {
    // Default — open a `mailto:` to the sales team. NEU can later replace
    // this with a Zendesk widget or a contact form route.
    const subject = encodeURIComponent(String(t('stripeUpgrade.schoolLicense.mailSubject')));
    window.location.href = `mailto:schulen@meinunterricht.de?subject=${subject}`;
  };

  const renderBody = () => {
    if (loadError) {
      return (
        <Box sx={{ p: spaces.lg, textAlign: 'center' }}>
          <Alert severity='error' data-testid='stripe-upgrade-load-error'>
            {t('stripeUpgrade.loadFailed')}
          </Alert>
        </Box>
      );
    }

    if (!optionsResult) {
      return (
        <Box
          sx={{
            p: spaces.lg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spaces.md }}>
            <CircularProgress
              data-testid='stripe-upgrade-loading'
              sx={{ '& .MuiCircularProgress-svg': { width: '100%', height: '100%' } }}
            />
            <Typography variant='body2'>{t('stripeUpgrade.loadingOptions')}</Typography>
          </Box>
        </Box>
      );
    }

    const { currentPlanKey, options } = optionsResult;
    // SM-391: pin column order FE-side (KI first, Pro second) — the kimuno
    // response returns options in no guaranteed order.
    const orderedOptions = orderStripeUpgradeOptions(options);

    return (
      <Box
        sx={{
          paddingY: spaces.xxl,
          paddingX: spaces.lg,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.md,
            paddingBottom: spaces.xl,
          }}
        >
          <Typography variant='h5' sx={{ textAlign: 'center', color: palette.text.primary }}>
            {t('Find the right plan for your goals')}
          </Typography>
          <Typography variant='subtitle1' sx={{ textAlign: 'center', color: palette.text.secondary }}>
            {t('Tens of thousands of teachers and hundreds of schools already trust us')}
          </Typography>
        </Box>
        <Box
          data-testid='stripe-upgrade-row'
          sx={{
            display: 'flex',
            flexDirection: isDesktopScreen ? 'row' : 'column',
            justifyContent: 'center',
            alignItems: isDesktopScreen ? 'stretch' : 'center',
            gap: isDesktopScreen ? spaces.md : spaces.xl,
          }}
        >
          {/* SM-202: basis users have no meaningful "current plan" to
              contrast the upgrade options against — the card only echoes
              "Kostenlos" + a single "eingeschränkter Zugriff" line, which
              adds noise without informing the choice. Hide it for that
              cohort and let the upgrade cards fill the row. */}
          {currentPlanKey !== 'basis' && (
            <StripeCurrentPlanCard currentPlanKey={currentPlanKey} />
          )}
          {orderedOptions.map((option) => (
            <StripeUpgradeOptionCard
              key={option.targetPlanKey}
              targetPlanKey={option.targetPlanKey}
              newMonthlyPrice={option.newMonthlyPrice}
              isMostPopular={option.targetPlanKey === STRIPE_MOST_POPULAR_PLAN}
              onConfirm={() => handleSelectPlan(option.targetPlanKey)}
              secondaryCta={getRefRabattCta(option.targetPlanKey)}
            />
          ))}
          {/* SM-92 Phase 6: the school-licence enquiry block is shown for
              the top two tiers (Pro and Kombi/360) — these users are the
              realistic audience for a multi-seat school-licence conversation.
              Lower tiers (basis, courseOnly, aiOnly) see only the upgrade
              cards. */}
          {(currentPlanKey === 'pro' || currentPlanKey === 'fullAccess') && (
            <StripeSchoolLicenseCta onContact={handleSchoolLicenseClick} />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <MasterLayout forceNewNavigation sidebarCollapsed>
      <Box
        data-testid='stripe-upgrade-page'
        sx={{ backgroundColor: palette.common.white, minHeight: '100%' }}
      >
        {renderBody()}
      </Box>
    </MasterLayout>
  );
};

export default StripeUpgradePage;
